import { ServiceProviderError } from '@takeshape/errors';
import { isServiceConfigWithOAuth2Authentication } from '@takeshape/schema';
import { getBasicAuthString, isRecord } from '@takeshape/util';
import { isOAuth2TokenResponse } from "../types.js";
export function getAuthInfoRequestConfigForClientCredentials(providerName) {
    return (serviceConfig) => {
        if (!isServiceConfigWithOAuth2Authentication(serviceConfig)) {
            throw new ServiceProviderError(`${providerName} - invalid service config`);
        }
        const { accessTokenUrl, clientId, clientSecret, scope } = serviceConfig.authentication;
        let sendClientCredentialsViaBasicAuth = false;
        const authOverrides = serviceConfig.options?.authentication;
        if (isRecord(authOverrides) && typeof authOverrides.sendClientCredentialsViaBasicAuth === 'boolean') {
            sendClientCredentialsViaBasicAuth = authOverrides.sendClientCredentialsViaBasicAuth;
        }
        const form = {
            grant_type: 'client_credentials'
        };
        let headers;
        if (sendClientCredentialsViaBasicAuth) {
            headers = {
                Authorization: `Basic ${getBasicAuthString({ username: clientId, password: clientSecret })}`
            };
        }
        else {
            form.client_id = clientId;
            if (clientSecret) {
                form.client_secret = clientSecret;
            }
        }
        if (scope) {
            form.scope = scope;
        }
        return {
            url: accessTokenUrl,
            method: 'POST',
            form,
            headers
        };
    };
}
export function getAuthInfoRequestResultsForClientCredentials(providerName) {
    return (serviceConfig, response) => {
        if (!isOAuth2TokenResponse(response)) {
            throw new ServiceProviderError(`${providerName} - invalid auth response`);
        }
        let tokenPath = ['headers', 'Authorization'];
        let prefix = response.token_type;
        const authOverrides = serviceConfig.options?.authentication;
        if (isRecord(authOverrides)) {
            if (Array.isArray(authOverrides.tokenPath)) {
                tokenPath = authOverrides.tokenPath;
            }
            prefix = typeof authOverrides.prefix === 'string' ? authOverrides.prefix : '';
        }
        // Hopefully we can trust this, though it might take more work
        // to handle all cases. https://tools.ietf.org/html/rfc6749#section-7.1
        return [[tokenPath, prefix ? `${prefix} ${response.access_token}` : response.access_token]];
    };
}
const offset = 300; // 5 mins
const defaultExpiration = 120; // 2 mins
export function getAuthInfoCacheExpirationForClientCredentials(providerName) {
    return (_, response) => {
        if (!isOAuth2TokenResponse(response)) {
            throw new ServiceProviderError(`${providerName} - invalid auth response`);
        }
        return Number(response.expires_in ?? offset + defaultExpiration) - offset;
    };
}
export function getOAuth2ClientCredentialsDefaults(title) {
    return {
        getAuthInfoCacheExpiration: getAuthInfoCacheExpirationForClientCredentials(title),
        getAuthInfoRequestConfig: getAuthInfoRequestConfigForClientCredentials(title),
        getAuthInfoResult: getAuthInfoRequestResultsForClientCredentials(title)
    };
}
